import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import PaymentGatewaySelector from '../components/PaymentGatewaySelector';

const ModernBooking = () => {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const [bookingForm, setBookingForm] = useState({
    adults: 1,
    children: 0,
    visitDate: '',
    customerDetails: { name: '', email: '', phone: '' }
  });
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [bookingId, setBookingId] = useState(null);

  useEffect(() => {
    fetchTickets();
    setMinVisitDate();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/public/test-tickets`);
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
  };

  const setMinVisitDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setBookingForm(prev => ({
      ...prev,
      visitDate: tomorrow.toISOString().split('T')[0]
    }));
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedGateway) {
      alert('Please select a payment gateway');
      return;
    }
    
    try {
      const adultPrice = selectedTicket.price;
      const childPrice = Math.round(selectedTicket.price * 0.7);
      const totalAmount = (adultPrice * bookingForm.adults) + (childPrice * bookingForm.children);
      const totalQuantity = bookingForm.adults + bookingForm.children;
      
      const bookingData = {
        ticketTypeId: selectedTicket._id,
        quantity: totalQuantity,
        adults: bookingForm.adults,
        children: bookingForm.children,
        customerName: bookingForm.customerDetails.name,
        customerEmail: bookingForm.customerDetails.email,
        customerPhone: bookingForm.customerDetails.phone,
        totalAmount: totalAmount,
        gatewayId: selectedGateway._id
      };

      const response = await axios.post(`${API_BASE_URL}/api/test-bookings/create`, bookingData);
      
      if (response.data.success && response.data.paymentUrl) {
        setBookingId(response.data.booking.bookingId);
        setShowPaymentModal(true);
        
        const paymentWindow = window.open(response.data.paymentUrl, '_blank', 'width=1000,height=700');
        
        // Check if payment window is closed
        const checkClosed = setInterval(() => {
          if (paymentWindow.closed) {
            clearInterval(checkClosed);
            // Wait a moment then redirect to check status
            setTimeout(() => {
              window.location.href = `/payment-success?bookingId=${response.data.booking.bookingId}&amount=${totalAmount}`;
            }, 2000);
          }
        }, 1000);
      }
    } catch (error) {
      alert('Booking failed: ' + (error.response?.data?.message || error.message));
    }
  };

  const totalAmount = selectedTicket ? 
    (selectedTicket.price * bookingForm.adults) + (Math.round(selectedTicket.price * 0.7) * bookingForm.children) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              🎫 Book Your Experience
            </h1>
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <span className={`px-3 py-1 rounded-full ${currentStep >= 1 ? 'bg-purple-100 text-purple-700' : 'bg-gray-100'}`}>
                1. Select
              </span>
              <span className={`px-3 py-1 rounded-full ${currentStep >= 2 ? 'bg-purple-100 text-purple-700' : 'bg-gray-100'}`}>
                2. Details
              </span>
              <span className={`px-3 py-1 rounded-full ${currentStep >= 3 ? 'bg-purple-100 text-purple-700' : 'bg-gray-100'}`}>
                3. Payment
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {!selectedTicket ? (
          /* Step 1: Ticket Selection */
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Choose Your Adventure</h2>
              <p className="text-gray-600">Select from our amazing collection of experiences</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tickets.slice(0, 9).map(ticket => (
                <div
                  key={ticket._id}
                  onClick={() => {
                    setSelectedTicket(ticket);
                    setCurrentStep(2);
                  }}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group overflow-hidden"
                >
                  <div className="h-48 bg-gradient-to-br from-purple-400 to-blue-500 relative">
                    <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                    <div className="absolute bottom-4 left-4 text-white">
                      <div className="text-2xl mb-2">
                        {ticket.name.includes('Beach') ? '🏖️' : 
                         ticket.name.includes('Temple') ? '🛕' : 
                         ticket.name.includes('Lake') ? '🏞️' : '🌳'}
                      </div>
                    </div>
                    <div className="absolute top-4 right-4">
                      <span className="bg-white bg-opacity-90 text-purple-700 px-3 py-1 rounded-full text-sm font-semibold">
                        ₹{ticket.price}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-bold text-lg text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">
                      {ticket.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      {ticket.city} • {ticket.category.replace('_', ' ')}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-500">
                        👥 Adult: ₹{ticket.price} • Child: ₹{Math.round(ticket.price * 0.7)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Step 2 & 3: Booking Form */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Ticket Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-8">
                <button
                  onClick={() => {
                    setSelectedTicket(null);
                    setCurrentStep(1);
                  }}
                  className="text-purple-600 hover:text-purple-700 mb-4 flex items-center text-sm font-medium"
                >
                  ← Back to selection
                </button>

                <div className="h-32 bg-gradient-to-br from-purple-400 to-blue-500 rounded-xl mb-4 flex items-center justify-center">
                  <span className="text-4xl">
                    {selectedTicket.name.includes('Beach') ? '🏖️' : 
                     selectedTicket.name.includes('Temple') ? '🛕' : 
                     selectedTicket.name.includes('Lake') ? '🏞️' : '🌳'}
                  </span>
                </div>

                <h3 className="font-bold text-xl text-gray-900 mb-2">{selectedTicket.name}</h3>
                <p className="text-gray-600 mb-4">{selectedTicket.city}</p>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">👨👩👧👦 Adults ({bookingForm.adults})</span>
                    <span className="font-semibold">₹{selectedTicket.price * bookingForm.adults}</span>
                  </div>
                  {bookingForm.children > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">👶 Children ({bookingForm.children})</span>
                      <span className="font-semibold">₹{Math.round(selectedTicket.price * 0.7) * bookingForm.children}</span>
                    </div>
                  )}
                  <div className="border-t pt-3">
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total</span>
                      <span className="text-purple-600">₹{totalAmount}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 rounded-xl p-4">
                  <div className="flex items-center text-purple-700 mb-2">
                    <span className="text-lg mr-2">✨</span>
                    <span className="font-semibold">What's Included</span>
                  </div>
                  <ul className="text-sm text-purple-600 space-y-1">
                    <li>• Entry to all attractions</li>
                    <li>• Valid for selected date</li>
                    <li>• Instant confirmation</li>
                    <li>• Digital ticket with QR code</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Right: Booking Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleBookingSubmit} className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Complete Your Booking</h2>

                {/* Quantity Selection */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">👥 How many tickets?</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Adults</label>
                      <div className="flex items-center border border-gray-300 rounded-lg">
                        <button
                          type="button"
                          onClick={() => setBookingForm({...bookingForm, adults: Math.max(1, bookingForm.adults - 1)})}
                          className="p-3 hover:bg-gray-50 text-gray-600"
                        >
                          −
                        </button>
                        <span className="flex-1 text-center py-3 font-semibold">{bookingForm.adults}</span>
                        <button
                          type="button"
                          onClick={() => setBookingForm({...bookingForm, adults: Math.min(10, bookingForm.adults + 1)})}
                          className="p-3 hover:bg-gray-50 text-gray-600"
                        >
                          +
                        </button>
                      </div>
                      <p className="text-xs text-purple-600 mt-1">₹{selectedTicket.price} per adult</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Children</label>
                      <div className="flex items-center border border-gray-300 rounded-lg">
                        <button
                          type="button"
                          onClick={() => setBookingForm({...bookingForm, children: Math.max(0, bookingForm.children - 1)})}
                          className="p-3 hover:bg-gray-50 text-gray-600"
                        >
                          −
                        </button>
                        <span className="flex-1 text-center py-3 font-semibold">{bookingForm.children}</span>
                        <button
                          type="button"
                          onClick={() => setBookingForm({...bookingForm, children: Math.min(10, bookingForm.children + 1)})}
                          className="p-3 hover:bg-gray-50 text-gray-600"
                        >
                          +
                        </button>
                      </div>
                      <p className="text-xs text-purple-600 mt-1">₹{Math.round(selectedTicket.price * 0.7)} per child</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Visit Date</label>
                      <input
                        type="date"
                        value={bookingForm.visitDate}
                        onChange={(e) => setBookingForm({...bookingForm, visitDate: e.target.value})}
                        min={new Date().toISOString().split('T')[0]}
                        required
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Customer Details */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">👤 Your Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Full Name *"
                      value={bookingForm.customerDetails.name}
                      onChange={(e) => setBookingForm({
                        ...bookingForm,
                        customerDetails: {...bookingForm.customerDetails, name: e.target.value}
                      })}
                      required
                      className="p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <input
                      type="email"
                      placeholder="Email Address"
                      value={bookingForm.customerDetails.email}
                      onChange={(e) => setBookingForm({
                        ...bookingForm,
                        customerDetails: {...bookingForm.customerDetails, email: e.target.value}
                      })}
                      className="p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <input
                      type="tel"
                      placeholder="Phone Number *"
                      value={bookingForm.customerDetails.phone}
                      onChange={(e) => {
                        const phone = e.target.value.replace(/[^0-9]/g, '');
                        if (phone.length <= 10) {
                          setBookingForm({
                            ...bookingForm,
                            customerDetails: {...bookingForm.customerDetails, phone}
                          });
                        }
                      }}
                      required
                      className="p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent md:col-span-2"
                    />
                  </div>
                </div>

                {/* Payment Gateway */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">💳 Payment Method</h3>
                  <PaymentGatewaySelector 
                    onSelect={setSelectedGateway}
                    selectedGateway={selectedGateway}
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={!selectedGateway}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-400 text-white py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed"
                >
                  {selectedGateway ? `Pay ₹${totalAmount} & Book Now` : 'Select Payment Method'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Payment Waiting Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 text-center">
            <div className="mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <span className="text-2xl text-white">💳</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Payment in Progress</h3>
              <p className="text-gray-600">Please complete your payment in the opened window</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">Booking ID:</span>
                <span className="font-mono text-sm font-semibold">{bookingId}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Amount:</span>
                <span className="font-bold text-purple-600">₹{totalAmount}</span>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                <span className="ml-2">Waiting for payment...</span>
              </div>
              
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  window.location.href = `/payment-success?bookingId=${bookingId}&amount=${totalAmount}`;
                }}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 rounded-lg text-sm transition-colors"
              >
                Check Payment Status
              </button>
              
              <button
                onClick={() => setShowPaymentModal(false)}
                className="w-full text-gray-500 hover:text-gray-700 py-2 text-sm transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModernBooking;